import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { accessControlAction } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useDeviceStore } from '@/stores/deviceStore';
import { useEffect } from 'react';
import type { Device, DeviceAssignment } from '@/types';

export function useDevices() {
  const { user } = useAuth();
  const { setAssignments, setActiveDevice, activeDeviceId } = useDeviceStore();

  const query = useQuery({
    queryKey: ['devices', user?.id],
    queryFn: async (): Promise<DeviceAssignment[]> => {
      if (!user) return [];

      // 1. Direkta per-enhet-tilldelningar
      const { data, error } = await supabase
        .from('device_user_assignments')
        .select(`
          id,
          user_id,
          device_id,
          organization_id,
          can_generate_keys,
          can_share_keys,
          can_manage_access,
          key_type,
          access_days,
          access_start_time,
          access_end_time,
          valid_from,
          valid_until,
          device:ziplink_devices (
            id,
            device_code,
            name,
            location,
            nfc_enabled,
            qr_enabled,
            facility:facilities ( id, name )
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;

      // PostgREST bäddar in to-one-relationer som arrayer i den inferrerade typen;
      // normalisera både device och device.facility till enkla objekt.
      type RawDevice = Omit<Device, 'facility'> & {
        facility?: Device['facility'] | Array<{ id: string; name: string }>;
      };
      const normalizeDevice = (raw: unknown): Device | undefined => {
        const dev = (Array.isArray(raw) ? raw[0] : raw) as RawDevice | null | undefined;
        if (!dev) return undefined;
        return {
          ...dev,
          facility: Array.isArray(dev.facility) ? dev.facility[0] ?? null : dev.facility ?? null,
        };
      };

      const direct: DeviceAssignment[] = (data ?? []).map((row) => ({
        ...row,
        device: normalizeDevice(row.device),
        source: 'direct' as const,
      })) as DeviceAssignment[];

      // 2. Ärvda dörrar från två källor, hämtade additivt och parallellt: fel i
      //    en gren får inte dölja de direkta tilldelningarna eller den andra grenen.
      //    - get_my_facility_devices(): ärvda via tilldelning per anläggning
      //    - get_my_group_devices():    ärvda via medlemskap i en accessgrupp
      //    Båda utesluter redan dörrar med en direkt tilldelning (direct vinner).
      const [facilityResult, groupResult] = await Promise.all([
        supabase.rpc('get_my_facility_devices'),
        supabase.rpc('get_my_group_devices'),
      ]);

      if (facilityResult.error) {
        console.error('get_my_facility_devices failed:', facilityResult.error);
      }
      if (groupResult.error) {
        console.error('get_my_group_devices failed:', groupResult.error);
      }

      const facilityDevices: DeviceAssignment[] = (
        (facilityResult.data ?? []) as Array<Record<string, unknown>>
      ).map((row) => ({
        ...row,
        device: normalizeDevice(row.device),
        source: 'facility' as const,
      })) as DeviceAssignment[];

      const groupDevices: DeviceAssignment[] = (
        (groupResult.data ?? []) as Array<Record<string, unknown>>
      ).map((row) => ({
        ...row,
        device: normalizeDevice(row.device),
        source: 'group' as const,
      })) as DeviceAssignment[];

      // Dedupe på device_id. Direkt vinner; annars behålls första ärvda källan
      // (facility före group) så en dörr aldrig dubbleras i listan.
      const seen = new Set(direct.map((a) => a.device_id));
      const inherited: DeviceAssignment[] = [];
      for (const assignment of [...facilityDevices, ...groupDevices]) {
        if (seen.has(assignment.device_id)) continue;
        seen.add(assignment.device_id);
        inherited.push(assignment);
      }

      return [...direct, ...inherited];
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (query.data) {
      setAssignments(query.data);

      // Auto-select first device if none selected
      if (!activeDeviceId && query.data.length > 0 && query.data[0].device) {
        setActiveDevice(query.data[0].device);
      }

      // Restore persisted device
      if (activeDeviceId) {
        const match = query.data.find((a) => a.device_id === activeDeviceId);
        if (match?.device) {
          setActiveDevice(match.device);
        }
      }
    }
  }, [query.data, activeDeviceId, setAssignments, setActiveDevice]);

  return query;
}

export function useRemoveDevice() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { activeDeviceId, clearDevice } = useDeviceStore();

  return useMutation({
    mutationFn: async (assignmentId: string) => {
      const result = await accessControlAction<{ success: boolean }>(
        'remove-my-device',
        { assignment_id: assignmentId }
      );
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onMutate: async (assignmentId) => {
      await queryClient.cancelQueries({ queryKey: ['devices', user?.id] });
      const previous = queryClient.getQueryData<DeviceAssignment[]>(['devices', user?.id]);

      const removed = previous?.find((a) => a.id === assignmentId);
      queryClient.setQueryData<DeviceAssignment[]>(
        ['devices', user?.id],
        (old) => old?.filter((a) => a.id !== assignmentId) ?? []
      );

      // Clear active device if it was the removed one
      if (removed && removed.device_id === activeDeviceId) {
        clearDevice();
      }

      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['devices', user?.id], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['devices', user?.id] });
      // Backend återkallar enhetens nycklar när tilldelningen tas bort, så
      // uppdatera nyckellistan/"Aktiva nycklar" direkt istället för att vänta
      // på nästa polling-intervall.
      queryClient.invalidateQueries({ queryKey: ['all-keys', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['keys', user?.id] });
    },
  });
}
